import { ensureConditionArray } from './filter.model';

/**
 * The English labels of the filter properties, for texts built without the
 * translation (the labels of recent searches); the UI uses `filter.field.*`.
 */
export const FILTER_LABELS: Record<string, string> = {
  name: 'Name',
  sectors: 'Subsector',
  ownership_type: 'Ownership type',
  investors: 'Investor',
  keywords: 'Keywords',
  attractiveness_score: 'Score',
  review_status: 'Review status',
  founded_at: 'Founded',
  country_code: 'Country',
  city: 'City',
  last_funded_year: 'Latest funded year',
  last_investment_amount: 'Latest funded amount ($)',
  last_employee_count: 'Employees',
  last_employee_growth: 'Latest employee growth (%)',
  revenue: 'Revenue ($)',
  last_ebitda: 'Latest EBITDA ($)',
  reviewer: 'Reviewer',
  business_id: 'Companies of the alert',
};

/** One condition of the filter as a removable chip. */
export interface FilterChip {
  property: string;
  label: string;
  value: string;
}

const pad = (value: number) => String(value).padStart(2, '0');

const formatValue = (
  property: string,
  value: unknown,
  names: Map<number, string>
): string => {
  // The ids of the companies of a list alert say nothing, their number does.
  if (property === 'business_id' && Array.isArray(value)) {
    return value.length === 1 ? '1 company' : `${value.length} companies`;
  }
  if (Array.isArray(value)) {
    return value.map((x) => formatValue(property, x, names)).join(', ');
  }
  if (
    value instanceof Date ||
    (typeof value === 'string' && /^\d{4}-\d\d-\d\dT/.test(value))
  ) {
    const date = new Date(value as string | Date);
    if (isNaN(date.getTime())) {
      return String(value);
    }
    return property === 'last_funded_year'
      ? String(date.getFullYear())
      : `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }
  if (property === 'sectors' && typeof value === 'number') {
    return names.get(value) ?? String(value);
  }
  if (property === 'keywords' && typeof value === 'string') {
    return value.split('&').join(', ');
  }
  if (typeof value === 'number') {
    return value.toLocaleString('en-US');
  }
  return String(value ?? '');
};

/** The conditions of a filter of the companies filter as chips. */
export const toFilterChips = (
  filter: unknown[] | null | undefined,
  sectorNames: Map<number, string> = new Map(),
  translate?: (key: string) => string
): FilterChip[] =>
  (filter && filter.length ? ensureConditionArray(filter) : []).map(
    ([property, operator, value]) => {
      const text =
        operator === 'between' && Array.isArray(value)
          ? `${formatValue(property, value[0], sectorNames)} – ${formatValue(property, value[1], sectorNames)}`
          : `${operator === '>=' ? '≥ ' : operator === '<=' ? '≤ ' : ''}${formatValue(property, value, sectorNames)}`;
      return {
        property,
        label:
          translate && FILTER_LABELS[property]
            ? translate(`filter.field.${property}`)
            : (FILTER_LABELS[property] ?? property),
        value: text,
      };
    }
  );

/**
 * The filter without the conditions of a property, joined with "and" as the
 * filter panel joins them; an empty array when none is left.
 */
export const withoutFilterProperty = (
  filter: unknown[] | null | undefined,
  property: string
): unknown[] => {
  const rest = (
    filter && filter.length ? ensureConditionArray(filter) : []
  ).filter(([name]) => name !== property);
  return rest.length === 1
    ? rest[0]
    : rest.reduce(
        (acc: unknown[], cur, idx) => [
          ...acc,
          ...(idx > 0 ? ['and', cur] : [cur]),
        ],
        [] as unknown[]
      );
};

/**
 * The filter in one line, e.g. "Subsector: Payments, Country: DE"; null
 * without conditions.
 */
export const filterSummary = (
  filter: unknown[] | null | undefined,
  sectorNames: Map<number, string> = new Map(),
  translate?: (key: string) => string
): string | null =>
  toFilterChips(filter, sectorNames, translate)
    .map((chip) => `${chip.label}: ${chip.value}`)
    .join(', ') || null;
